import { motion } from "framer-motion";
import { FileJson, Cpu, Bot } from "lucide-react";

const steps = [
  {
    icon: FileJson,
    title: "Paste Any API Spec",
    desc: "Upload or paste your OpenAPI, Swagger, Postman, GraphQL or plain text — any format works.",
    color: "bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400",
    hoverBorder: "group-hover:border-blue-200 dark:group-hover:border-blue-800",
  },
  {
    icon: Cpu,
    title: "Convert to MCP",
    desc: "Our engine parses every endpoint and generates type-safe MCP tool definitions instantly.",
    color: "bg-violet-50 text-violet-600 dark:bg-violet-950 dark:text-violet-400",
    hoverBorder: "group-hover:border-violet-200 dark:group-hover:border-violet-800",
  },
  {
    icon: Bot,
    title: "Connect to Any AI Tool",
    desc: "Automatically connects to Claude, Cursor, Cline, Windsurf and more — one command.",
    color: "bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400",
    hoverBorder: "group-hover:border-emerald-200 dark:group-hover:border-emerald-800",
  },
];

const HowItWorks = () => {
  return (
    <section className="py-24 px-6 relative overflow-hidden">
      <div className="absolute inset-0 bg-muted/20 pointer-events-none" />

      <div className="container max-w-5xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-1.5 mb-4">
            <span className="text-xs font-semibold text-primary uppercase tracking-widest">How it works</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-foreground">
            Three Steps to AI-Ready
          </h2>
          <p className="text-muted-foreground text-base max-w-md mx-auto">
            From API spec to working MCP server in under a minute.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 relative">
          {/* Connector line */}
          <div className="hidden md:block absolute top-[3.5rem] left-[22%] right-[22%] h-px">
            <div className="h-full bg-gradient-to-r from-border via-primary/30 to-border" />
          </div>

          {steps.map((step, i) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15, duration: 0.6 }}
              className="group"
            >
              <div
                className={`glass-strong rounded-2xl p-8 shadow-premium card-lift border border-transparent transition-colors ${step.hoverBorder} text-center`}
              >
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    <div className={`w-14 h-14 rounded-2xl ${step.color} flex items-center justify-center`}>
                      <step.icon className="w-7 h-7" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-primary flex items-center justify-center text-white text-[11px] font-bold shadow-sm">
                      {i + 1}
                    </div>
                  </div>
                </div>
                <h3 className="text-base font-bold mb-3 text-foreground">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
